import { useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  MenuItem,
  Autocomplete,
  TextField,
  Checkbox,
} from '@mui/material';
import { useEffectAsync } from '../../reactHelper';
import fetchOrThrow from '../util/fetchOrThrow';

const SelectField = ({
  label,
  fullWidth,
  multiple,
  value = null,
  emptyValue = null,
  emptyTitle = '',
  onChange,
  endpoint,
  data,
  keyGetter = (item) => item.id,
  titleGetter = (item) => item.name,
  helperText,
  placeholder,
  searchPlaceholder,
  noOptionsText,
  loadingText,
  disabled,
}) => {
  const [items, setItems] = useState();
  const [searchValue, setSearchValue] = useState('');
  const baseSelectSx = {
    '& .MuiAutocomplete-inputRoot': {
      minHeight: 40,
    },
    '& .MuiAutocomplete-input': {
      minWidth: 64,
    },
  };

  const multipleSelectSx = {
    ...baseSelectSx,
    '& .MuiAutocomplete-inputRoot': {
      alignItems: 'flex-start',
      maxHeight: 120,
      overflowY: 'auto',
      overflowX: 'hidden',
      scrollbarWidth: 'thin',
    },
    '& .MuiAutocomplete-tag': {
      maxWidth: 'calc(100% - 8px)',
    },
    '& .MuiAutocomplete-tag .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };

  const getOptionKey = (option) => (typeof option === 'object' ? keyGetter(option) : option);

  const sortedItems = useMemo(() => items || [], [items]);
  const isLoading = endpoint && !items;

  const getOptionLabel = (option) => {
    if (typeof option !== 'object') {
      option = sortedItems.find((obj) => keyGetter(obj) === option);
    }
    return option ? titleGetter(option) : emptyTitle;
  };

  const multipleValue = (Array.isArray(value) ? value : [])
    .map((id) => sortedItems?.find((item) => keyGetter(item) === id))
    .filter(Boolean);
  const singleValue = sortedItems?.find((item) => keyGetter(item) === value) || null;

  useEffect(() => setItems(data), [data]);

  useEffectAsync(async () => {
    if (endpoint) {
      const response = await fetchOrThrow(endpoint);
      setItems(await response.json());
    }
  }, []);

  if (sortedItems) {
    return (
      <FormControl fullWidth={fullWidth}>
        {multiple ? (
          <Autocomplete
            multiple
            size="small"
            sx={multipleSelectSx}
            options={sortedItems}
            getOptionLabel={getOptionLabel}
            disableCloseOnSelect
            filterSelectedOptions
            limitTags={2}
            noOptionsText={noOptionsText}
            loading={isLoading}
            loadingText={loadingText || 'Loading...'}
            disabled={disabled}
            renderOption={(props, option, { selected }) => (
              <MenuItem {...props} key={keyGetter(option)} value={keyGetter(option)}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                {titleGetter(option)}
              </MenuItem>
            )}
            isOptionEqualToValue={(option, selectedValue) => getOptionKey(option) === getOptionKey(selectedValue)}
            value={multipleValue}
            inputValue={searchValue}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'reset') {
                return;
              }
              setSearchValue(newInputValue);
            }}
            onChange={(_, selectedValues) => onChange({
              target: {
                value: selectedValues.map((selectedValue) => keyGetter(selectedValue)),
              },
            })}
            renderInput={(params) => <TextField {...params} label={label} helperText={helperText} placeholder={searchPlaceholder || placeholder} />}
          />
        ) : (
          <Autocomplete
            size="small"
            sx={baseSelectSx}
            options={sortedItems}
            getOptionLabel={getOptionLabel}
            noOptionsText={noOptionsText}
            loading={isLoading}
            loadingText={loadingText || 'Loading...'}
            disabled={disabled}
            clearOnEscape
            renderOption={(props, option, { selected }) => (
              <MenuItem {...props} key={keyGetter(option)} value={keyGetter(option)}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                {titleGetter(option)}
              </MenuItem>
            )}
            isOptionEqualToValue={(option, selectedValue) => getOptionKey(option) === getOptionKey(selectedValue)}
            value={singleValue}
            inputValue={searchValue}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'reset') {
                return;
              }
              setSearchValue(newInputValue);
            }}
            onChange={(_, selectedValue) => onChange({ target: { value: selectedValue ? keyGetter(selectedValue) : emptyValue } })}
            renderInput={(params) => <TextField {...params} label={label} helperText={helperText} placeholder={searchPlaceholder || placeholder} />}
          />
        )}
      </FormControl>
    );
  }
  return null;
};

export default SelectField;
